import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import EditorialSubNav from '../components/EditorialSubNav';
import { useT } from '../i18n/LanguageContext';

const eyebrowStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '9pt',
  fontWeight: 600,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: '#D6A87A',
};

const SectionEyebrow = ({ children, align = 'left' }) => (
  <div
    style={{ ...eyebrowStyle, textAlign: align }}
    className="mb-6"
  >
    {children}
  </div>
);

/* ── Monoline copper city silhouettes ── */
const GalataTowerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2.5L9 6.5h6L12 2.5z"
      stroke="#D6A87A"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path
      d="M9 6.5v2.5h6V6.5"
      stroke="#D6A87A"
      strokeWidth="1.2"
    />
    <rect
      x="9.5"
      y="9"
      width="5"
      height="11"
      stroke="#D6A87A"
      strokeWidth="1.2"
    />
    <path d="M11 12h2M11 14.5h2M11 17h2" stroke="#D6A87A" strokeWidth="1" strokeLinecap="round" />
    <path d="M7 20h10" stroke="#D6A87A" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const BurjKhalifaIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2L11.6 6 10 8 9.4 12 8 14 7.4 18 6 20h12l-1.4-2-.6-4-1.4-2-.6-4L12.4 6 12 2z"
      stroke="#D6A87A"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path
      d="M11.6 6h.8M10 8h4M9.4 12h5.2M8 14h8"
      stroke="#D6A87A"
      strokeWidth="1"
      strokeLinecap="round"
    />
  </svg>
);

const CompassRoseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="#D6A87A" strokeWidth="1.2" />
    <path
      d="M12 4l1.6 6.4L20 12l-6.4 1.6L12 20l-1.6-6.4L4 12l6.4-1.6L12 4z"
      stroke="#D6A87A"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const LinkedInIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="#D6A87A" strokeWidth="1.5" />
    <path
      d="M8 10v7M8 7v.01M12 17v-4a2 2 0 0 1 4 0v4"
      stroke="#D6A87A"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const Studio = () => {
  const t = useT();
  const subNavLinks = [
    { label: t('subnav.studio'), target: 'studio' },
    { label: t('subnav.accountManager'), target: 'account-manager' },
    { label: t('subnav.founder'), target: 'founder' },
    { label: t('subnav.offices'), target: 'offices' },
    { label: t('subnav.contact'), target: 'contact' },
  ];

  return (
    <div className="bg-bone text-ink min-h-screen" data-testid="studio-page">
      <SEO
        title="The Studio, A Mervin Travel brand | GlowMeets"
        description="Two floors, one delivery captain. GlowMeets is the dedicated MICE, events and destination-wedding brand of Mervin Travel, a Tursab-licensed inbound DMC since 2014."
        keywords="GlowMeets studio, Mervin Travel, Tursab licensed DMC, event design studio Istanbul, founder Ahmet Kazancı"
        url="/studio"
      />

      {/* ───────────────────────── 1. STUDIO HERO ───────────────────────── */}
      <section
        className="relative w-full overflow-hidden bg-navy"
        style={{ minHeight: '100vh' }}
        data-testid="studio-hero"
      >
        <img
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=2200&q=80"
          alt="GlowMeets design studio, moodboards, fabric swatches, lighting samples"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Horizontal duotone fade, navy left, photo right */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to right, rgba(10,19,47,0.94) 0%, rgba(10,19,47,0.86) 38%, rgba(10,19,47,0.5) 65%, rgba(10,19,47,0.18) 100%)',
          }}
        />
        {/* Bottom soft fade for transition */}
        <div
          className="absolute inset-x-0 bottom-0"
          style={{
            height: '180px',
            background:
              'linear-gradient(to bottom, rgba(10,19,47,0) 0%, #0A132F 100%)',
          }}
        />

        <div
          className="relative z-10 max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex items-center"
          style={{
            minHeight: '100vh',
            paddingTop: '120px',
            paddingBottom: '90px',
          }}
        >
          <div className="max-w-[58ch]">
            <SectionEyebrow>{t('studio.hero.eyebrow')}</SectionEyebrow>

            <h1
              className="text-bone"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(40px, 5vw, 64pt)',
                lineHeight: 1.05,
                fontWeight: 500,
                letterSpacing: '-0.005em',
              }}
            >
              <span className="block">{t('studio.hero.line1')}</span>
              <span
                className="block italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {t('studio.hero.line2')}
              </span>
            </h1>

            <p
              className="mt-8"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: '18pt',
                color: 'rgba(244,239,227,0.82)',
                lineHeight: 1.5,
                maxWidth: '60ch',
              }}
            >
              {t('studio.hero.sub')}
            </p>

            <div
              className="mt-10"
              style={{
                height: '1px',
                width: '88px',
                backgroundColor: 'rgba(214,168,122,0.55)',
              }}
            />
          </div>
        </div>
      </section>

      {/* Sticky in-page sub-nav (bone surface, sits above the bone "Studio" body) */}
      <EditorialSubNav links={subNavLinks} surface="bone" />

      {/* ───────────────────────── 2. THE STUDIO BODY ───────────────────────── */}
      <section
        id="studio"
        className="bg-bone text-ink"
        style={{ paddingTop: '100px', paddingBottom: '100px' }}
        data-testid="studio-body"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-[70ch]">
            <SectionEyebrow>{t('studio.body.eyebrow')}</SectionEyebrow>
            <h2
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(28px, 2.8vw, 36pt)',
                lineHeight: 1.15,
                fontWeight: 500,
                color: '#0A132F',
                letterSpacing: '-0.005em',
              }}
            >
              {t('studio.body.headline1')}{' '}
              <em
                className="italic"
                style={{ color: '#B98556', fontWeight: 400 }}
              >
                {t('studio.body.headline2')}
              </em>
            </h2>

            <div
              className="mt-10 space-y-6"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '13pt',
                fontWeight: 400,
                lineHeight: 1.7,
                color: '#4A4F63',
              }}
            >
              {(t('studio.body.paragraphs') || []).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <div
              className="mt-10"
              style={{
                height: '1px',
                width: '64px',
                backgroundColor: 'rgba(214,168,122,0.55)',
              }}
            />

            <p
              className="mt-8"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: '14pt',
                color: '#D6A87A',
                lineHeight: 1.5,
                letterSpacing: '0.005em',
              }}
            >
              {t('common.tursab')}
            </p>
          </div>
        </div>
      </section>

      {/* ───────────────────────── 3. ACCOUNT MANAGER PROMISE ───────────────────────── */}
      <section
        id="account-manager"
        className="bg-navy text-bone w-full"
        style={{ paddingTop: '100px', paddingBottom: '100px' }}
        data-testid="studio-account-manager"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <SectionEyebrow align="center">
              {t('studio.accountManager.eyebrow')}
            </SectionEyebrow>
            <h2
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(30px, 3vw, 38pt)',
                lineHeight: 1.1,
                fontWeight: 500,
                color: '#F4EFE3',
              }}
            >
              {t('studio.accountManager.headline1')}{' '}
              <em
                className="italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {t('studio.accountManager.headline2')}
              </em>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 max-w-5xl mx-auto">
            {(t('studio.accountManager.paragraphs') || []).map((p, i) => (
              <p
                key={i}
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '12pt',
                  fontWeight: 400,
                  lineHeight: 1.7,
                  color: 'rgba(244,239,227,0.82)',
                  maxWidth: '480px',
                }}
              >
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────── 4. FOUNDER'S NOTE ───────────────────────── */}
      <section
        id="founder"
        className="bg-bone text-ink"
        style={{ paddingTop: '120px', paddingBottom: '120px' }}
        data-testid="studio-founder"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* LEFT, portrait + identifier */}
            <div className="lg:col-span-4">
              <div
                className="relative w-full overflow-hidden"
                style={{
                  aspectRatio: '3/4',
                  backgroundColor: '#0A132F',
                  border: '1px solid rgba(214,168,122,0.35)',
                }}
                data-testid="founder-portrait"
              >
                <img
                  src="/founder-640.jpg"
                  srcSet="/founder-320.jpg 320w, /founder-640.jpg 640w"
                  sizes="(max-width: 640px) 320px, 480px"
                  alt="Ahmet Kazancı, founder of GlowMeets"
                  className="absolute inset-0 w-full h-full"
                  style={{ objectFit: 'cover', objectPosition: 'center 22%' }}
                />
                <div
                  className="absolute bottom-0 inset-x-0 pointer-events-none"
                  style={{
                    height: '38%',
                    background:
                      'linear-gradient(to top, rgba(10,19,47,0.55), rgba(10,19,47,0))',
                  }}
                  aria-hidden="true"
                />
              </div>

              {/* Identifier */}
              <div className="mt-6">
                <div
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '9pt',
                    fontWeight: 600,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: '#D6A87A',
                  }}
                >
                  Ahmet Kazancı
                </div>
                <div
                  className="mt-1"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '10pt',
                    fontWeight: 400,
                    color: '#0A132F',
                    lineHeight: 1.5,
                  }}
                >
                  {t('studio.founder.role')}
                </div>

                <a
                  href="https://www.linkedin.com/in/ahmetkazanci/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Ahmet Kazancı on LinkedIn"
                  className="inline-flex items-center mt-4 hover:opacity-70 transition-opacity"
                  data-testid="founder-linkedin"
                >
                  <LinkedInIcon size={18} />
                </a>
              </div>
            </div>

            {/* RIGHT, Founder's note */}
            <div className="lg:col-span-8">
              <SectionEyebrow>{t('studio.founder.eyebrow')}</SectionEyebrow>
              <h3
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: 'clamp(26px, 2.4vw, 30pt)',
                  lineHeight: 1.15,
                  fontWeight: 500,
                  color: '#0A132F',
                  letterSpacing: '-0.005em',
                }}
              >
                {t('studio.founder.headline1')}{' '}
                <em
                  className="italic"
                  style={{ color: '#B98556', fontWeight: 400 }}
                >
                  {t('studio.founder.headline2')}
                </em>
              </h3>

              <p
                className="mt-8"
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontStyle: 'italic',
                  fontWeight: 400,
                  fontSize: '16pt',
                  lineHeight: 1.55,
                  color: '#0A132F',
                  letterSpacing: '-0.002em',
                  maxWidth: '60ch',
                }}
              >
                {t('studio.founder.note')}
              </p>

              <div
                className="mt-8"
                style={{
                  height: '1px',
                  width: '40px',
                  backgroundColor: 'rgba(214,168,122,0.55)',
                }}
              />

              <p
                className="mt-5"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '11pt',
                  fontWeight: 500,
                  color: '#0A132F',
                  letterSpacing: '0.02em',
                }}
              >
                {t('studio.founder.signoff')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────────── 5. THREE OFFICES ───────────────────────── */}
      <section
        id="offices"
        className="text-bone w-full"
        style={{
          paddingTop: '90px',
          paddingBottom: '90px',
          background:
            'linear-gradient(180deg, #0E1B3F 0%, #060B22 100%)',
        }}
        data-testid="studio-offices"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <SectionEyebrow align="center">
              {t('studio.offices.eyebrow')}
            </SectionEyebrow>
            <h2
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(26px, 2.6vw, 30pt)',
                lineHeight: 1.15,
                fontWeight: 500,
                color: '#F4EFE3',
                letterSpacing: '-0.005em',
              }}
            >
              {t('studio.offices.headline1')}{' '}
              <em
                className="italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {t('studio.offices.headline2')}
              </em>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-12 md:gap-y-0">
            {/* İSTANBUL */}
            <div
              className="text-center md:px-8"
              style={{ borderRight: '1px solid rgba(214,168,122,0.35)' }}
              data-testid="office-istanbul"
            >
              <h3
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: '22pt',
                  fontWeight: 500,
                  color: '#D6A87A',
                  letterSpacing: '0.02em',
                }}
                className="mb-5"
              >
                İstanbul
              </h3>
              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '10pt',
                  fontWeight: 400,
                  color: 'rgba(244,239,227,0.75)',
                  lineHeight: 1.6,
                }}
              >
                Premier Kampüs Ofis, Suite 437
                <br />
                Gursel Mahallesi İmrahor Caddesi
                <br />
                No.29B, Kağıthane, Istanbul, Türkiye
              </p>
              <div className="mt-6 flex justify-center">
                <GalataTowerIcon />
              </div>
            </div>

            {/* DUBAI */}
            <div
              className="text-center md:px-8"
              style={{ borderRight: '1px solid rgba(214,168,122,0.35)' }}
              data-testid="office-dubai"
            >
              <h3
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: '22pt',
                  fontWeight: 500,
                  color: '#D6A87A',
                  letterSpacing: '0.02em',
                }}
                className="mb-5"
              >
                Dubai
              </h3>
              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '10pt',
                  fontWeight: 400,
                  color: 'rgba(244,239,227,0.75)',
                  lineHeight: 1.6,
                }}
              >
                OneJLT, 3rd Floor
                <br />
                Jumeirah Lakes Towers
                <br />
                Dubai, UAE
              </p>
              <div className="mt-6 flex justify-center">
                <BurjKhalifaIcon />
              </div>
            </div>

            {/* DELAWARE */}
            <div
              className="text-center md:px-8"
              data-testid="office-delaware"
            >
              <h3
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: '22pt',
                  fontWeight: 500,
                  color: '#D6A87A',
                  letterSpacing: '0.02em',
                }}
                className="mb-5"
              >
                Delaware
              </h3>
              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '10pt',
                  fontWeight: 400,
                  color: 'rgba(244,239,227,0.75)',
                  lineHeight: 1.6,
                }}
              >
                1111B South Governors Avenue
                <br />
                Suite 6892
                <br />
                Dover DE 19904, USA
              </p>
              <div className="mt-6 flex justify-center">
                <CompassRoseIcon />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────────── 6. CLOSING BAND ───────────────────────── */}
      <section
        id="contact"
        className="bg-navy text-bone w-full text-center"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="studio-closing"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center">
          <img
            src="/glowmeets-mark.png"
            alt="GlowMeets compass mark"
            style={{ height: '56px', width: 'auto' }}
          />

          <p
            className="mt-7"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: '14pt',
              color: 'rgba(244,239,227,0.7)',
              lineHeight: 1.5,
            }}
          >
            {t('studio.closing')}
          </p>

          <Link
            to="/contact"
            data-testid="studio-cta"
            className="inline-flex items-center justify-center mt-8 transition-colors"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '13pt',
              fontWeight: 600,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#D6A87A',
              backgroundColor: 'transparent',
              border: '1px solid #D6A87A',
              padding: '0 28px',
              borderRadius: '999px',
              minWidth: '280px',
              height: '56px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#D6A87A';
              e.currentTarget.style.color = '#0A132F';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#D6A87A';
            }}
          >
            {t('cta.bookConsultation')}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Studio;
